import type { DocumentRecord } from '@/types/catalog'
import type { ConnectorRecord } from '@/types/connector'

export const GRID_LAYOUT = {
  COLUMNS: 2,
  MARGIN: 40,
  LEFT_OFFSET: 80,
} as const

export const DOCS_PER_PAGE = 4 // Documents rendered per canvas page (2×2 grid)

/**
 * Look up the upload timestamp for a board item by id. Prefers
 * `upload_started_at` (when the user actually picked the file) and falls back
 * to `created_at`. Returns null when no record is found, which sorts to the
 * end so unresolved items don't shuffle with real ones.
 */
function getUploadTimestamp(
  id: string,
  documents: DocumentRecord[],
  connectors: ConnectorRecord[],
): string | null {
  const doc = documents.find((d) => d.id === id)
  if (doc) return doc.upload_started_at ?? doc.created_at ?? null
  const connector = connectors.find((c) => c.id === id)
  if (connector) return connector.created_at ?? null
  return null
}

/**
 * Sort board items by the order the user uploaded them (ascending), with the
 * id as a deterministic tiebreaker. Items missing a timestamp sink to the end.
 *
 * Both BoardCanvas (page rendering) and boardSlice (blob preload) must use
 * the same order so prefetch matches what the user sees.
 */
export function sortByUploadOrder<T extends { document_id?: string; id?: string }>(
  items: T[],
  documents: DocumentRecord[],
  connectors: ConnectorRecord[],
): T[] {
  return [...items].sort((a, b) => {
    const aId = a.document_id ?? a.id ?? ''
    const bId = b.document_id ?? b.id ?? ''
    const aTs = getUploadTimestamp(aId, documents, connectors)
    const bTs = getUploadTimestamp(bId, documents, connectors)
    if (aTs && bTs) {
      if (aTs !== bTs) return aTs.localeCompare(bTs)
    } else if (aTs) {
      return -1
    } else if (bTs) {
      return 1
    }
    return aId.localeCompare(bId)
  })
}

/**
 * Resolve the blob document ID to preload for a board document.
 * Cross-checks the actual document record type to handle legacy data
 * where graph documents were saved with doc_type 'pnid'/'pfd' (ENG-2289).
 *
 * Returns the document ID to download, or null if no blob preload is needed.
 */
export function resolveBlobDocId(
  doc: { document_id?: string; id?: string; doc_type?: string; type?: string },
  documents: DocumentRecord[],
): string | null {
  const docId = doc.document_id ?? doc.id
  if (!docId) return null
  const storedType = doc.doc_type || doc.type
  const fullDoc = documents.find((d) => d.id === docId)
  const actualType = fullDoc?.type ?? storedType
  if (actualType === 'graph') {
    return fullDoc?.properties?.pnid ?? null
  }
  if (actualType === 'document' || actualType === 'pnid' || actualType === 'pfd') {
    return docId
  }
  return null
}
